import _ from "lodash";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import networkSelectors from "../../../selectors/network";
import { usePermitsAggregations } from "../../../components/filters/AggregationFilter";
import usePermitsFilters from "../../../components/filters/PermitsFilters";
import { useOmniFilter } from "../../../components/OmniFilter";
import { StreamTable, useGqlStreamQuery, useStreamQuery } from "../../../components/StreamTable";
import { useRenewalStreamQuery } from "../../../components/StreamTable/hooks";
import { useClient } from "../../../hooks/use-client";
import { usePersistedState } from "../../../hooks/use-persisted-state";
import { useStreamResource } from "../../../hooks/use-stream-resource";
import useUserPreference from "../../../hooks/use-user-preference";
import { getCitizenPermitsTypes } from "../../../queries/citizen";
import {
  fetchSearchPermits,
  fetchTransactionLogs,
  fetchPayouts,
  fetchRenewalSearchPermits,
  fetchInspections,
} from "../../../queries/search";
import ElasticSearch, { IElasticsearchSort } from "../../../utils/elasticsearch";
import { allMainColumns } from "../../../utils/variables";
import { Column } from "react-table";
import { LocationFieldType } from "../../../constants/LocationFilterTypes";
import { InspectionsCustomSortingConfiguration } from "../../../types/InspectionsCustomSort";

export const useCitizenPermitsTypes = () => {
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const permitsTypesQuery = useQuery("permitsTypes", getCitizenPermitsTypes(networkHeaders));
  const permitsTypes = useMemo(() => _.get(permitsTypesQuery.data, "data", []), [permitsTypesQuery.data]);
  return {
    query: permitsTypesQuery,
    data: permitsTypes,
  };
};

const defaultSort = {
  field: "dateEntered",
  direction: "desc",
} as IElasticsearchSort;

const defaultColumns = allMainColumns.filter((column) => {
  return column.id !== "active_workflow_steps" && column.id !== "subtotal";
});

export interface PermitsStreamTableColumnData {
  id: number;
  permitId: number;
  city_id: number;
  elasticsearch_updated_at: string;
  external_id: string;
  campaign_id: number;
  staff_user_id: number;
  official_documents_template_id: number;
  applicant_id: number;
  creator_id: number;
  creator: {
    address: string;
    city: string;
    email: string;
    firstName: string;
    lastName: string;
    full_name: string;
    phone: string;
    state: string;
    type: string;
    zip: string;
    department_name: string;
    departmentId: number;
  };
  dateEntered: string;
  submitted_at: string;
  issued_at: string;
  started_at: string;
  expired_at: string;
  expired_notified_at: string;
  lapsed_notified_at: string;
  deleted_at: string;
  updated_at: string;
  created_at: string;
  dateLastUpdated: string;
  payment_requested_at: string;
  non_renewal_reason_submitted_at: string;
  invalid_date: string;
  primary_identifier: string;
  reservation_number: string;
  reservation_number_normalized: string;
  confirmation_number: string;
  confirmation_number_normalized: string;
  parent_id: number;
  parent_reservation_number: string;
  parent_reservation_number_normalized: string;
  status: string;
  permits_type_id: number;
  permits_type_name: string;
  total_fee_in_cents: number;
  total_active_fee_in_cents: number;
  total_paid_in_cents: number;
  total_pending: number;
  subtotal: number;
  total_fee_with_waives_in_cents: number;
  total_fees: number;
  total_paid: number;
  customFields: {
    extensionId: number;
    id: number;
    name: string;
    value_string: string;
    value_string_keyword: string;
    value_int: number;
    value_date: string;
  }[];
  contacts: {
    address: string;
    address2: string;
    city: string;
    company_name: string;
    connected_user_first_name: string;
    connected_user_id: number;
    connected_user_last_name: string;
    connected_user_sub_type: string;
    contact_type: string;
    country_id: string;
    email: string;
    first_name: string;
    full_name: string;
    is_applicant: boolean;
    is_submitter: boolean;
    last_name: string;
    license_number: string;
    permits_extension_def_id: string;
    phone: string;
    physical_address: string;
    physical_address2: string;
    physical_city: string;
    physical_state: string;
    physical_zip: string;
    physical_country_id: string;
    same_mail_and_physical_address: boolean;
    state: string;
    zip: string;
  }[];
  primary_location_id: number;
  primary_location: {
    building_type: string;
    city_id: string;
    created_at: string;
    custom_field_values: {
      custom_field_id: string;
      custom_field_name: string;
      custom_field_type: LocationFieldType;
      custom_field_value_id: string;
      value_address: {
        address: string;
        address2: string;
        attn: string;
        city: string;
        full: string;
        id: string;
        state: string;
        zip: string;
      };
      value_boolean: boolean;
      value_datetime: string;
      value_keyword: string;
      value_number: number;
      value_text: string;
      value_text_array: {
        value: string;
      }[];
    }[];
    deactivated_at: string;
    id: string;
    location_address: {
      address: string;
      address2: string;
      attn: string;
      city: string;
      full: string;
      state: string;
      zip: string;
    };
    location_name: string;
    lot_size: string;
    owner_address: {
      address: string;
      address2: string;
      attn: string;
      city: string;
      full: string;
      state: string;
      zip: string;
    };
    owner_email: string;
    owner_name: string;
    owner_phone: string;
    parcel_number: string;
    updated_at: string;
    year_built: string;
    zoning: string;
  };
  assignee_ids: string[];
  assignees: {
    address: string;
    city: string;
    departmentId: number;
    email: string;
    firstName: string;
    full_name: string;
    lastName: string;
    phone: string;
    state: string;
    type: string;
    zip: string;
  }[];
  renewal_status: string;
  workflow_steps: {
    title: string;
  }[];
  active_workflow_steps: {
    title: string;
    step_type: string;
  }[];
  permits_attachments_count: 0;
  renewable: boolean;
  renewal_notify_mail: string;
  non_renewal_note: string;
  non_renewal_reason: string;
  issued_child: {
    confirmation_number: string;
    id: number;
    issued_at: string;
    reservation_number: string;
  };
  submitted_child: {
    confirmation_number: string;
    id: number;
    issued_at: string;
    reservation_number: string;
  };
}

export const PermitsStreamTable = (props: any & { columns: Column<PermitsStreamTableColumnData> }) => (
  <StreamTable<PermitsStreamTableColumnData> {...props} />
);

export const usePermits = (key: string, site: string) => {
  const { clientId } = useClient(site);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const viewsPreference = useUserPreference("permits.list.filters");
  const aggregations = usePermitsAggregations(clientId);
  const omnifilters = useOmniFilter(usePermitsFilters(viewsPreference, aggregations));
  const rowBuilder = (hits: any) => {
    return _.flatten(hits).map((item: any) => {
      const creatorDisplayName = _.join([item._source["creator.firstName"], item._source["creator.lastName"]], " ");
      return Object.assign({}, item._source, {
        creatorDisplayName,
      });
    });
  };
  const searchFn = fetchSearchPermits(networkHeaders);

  const streamQueryFn = (
    key: string,
    size: number,
    refreshCursor: any,
    setRefreshCursor: any,
    filter: any,
    reverseSort: any,
    sort: any,
    enabled: any,
    hits: any,
    setHits: any,
  ) =>
    useGqlStreamQuery({
      key: key,
      size: size,
      refreshCursor,
      setRefreshCursor,
      filter,
      reverseSort: reverseSort,
      sort: sort,
      enabled: enabled,
      queryFn: searchFn,
      hits,
      setHits,
    });

  return useStreamResource(
    key,
    "permits",
    omnifilters,
    streamQueryFn,
    rowBuilder,
    defaultSort,
    0,
    defaultColumns,
    true,
  );
};

const defaultTransactionSort = {
  field: "id",
  direction: "desc",
} as IElasticsearchSort;

export const useTransactions = (key: string, site: string, filters: any) => {
  const { clientId } = useClient(site);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const viewsPreference = useUserPreference("transaction.list.filters");
  const aggregations = usePermitsAggregations(clientId);
  const omnifilters = useOmniFilter(usePermitsFilters(viewsPreference, aggregations));
  const [refreshCursor, setRefreshCursor] = usePersistedState(`${key}.refreshCursor`, []);
  const rowBuilder = (hits: any) => {
    return _.flatten(hits).map((item: any) => ({ ...item._source }));
  };
  const [sort, setSort] = useState<IElasticsearchSort>(defaultTransactionSort);
  const streamSort = useMemo(() => ElasticSearch.sort(sort), [sort]);
  const streamReverseSort = useMemo(() => ElasticSearch.reverseSort(sort), [sort]);
  const searchFn = fetchTransactionLogs(networkHeaders);
  const streamQuery = useStreamQuery({
    key: key,
    size: 10,
    refreshCursor,
    setRefreshCursor,
    filter: filters,
    reverseSort: streamReverseSort,
    sort: streamSort,
    enabled: !!clientId,
    queryFn: searchFn,
    setHits: () => {
      return;
    },
  });
  const rows = useMemo(() => rowBuilder(streamQuery.hitsWindow), [streamQuery.hitsWindow]);
  return {
    streamQuery,
    rows,
    defaultSort,
    sort,
    setSort,
    omnifilters,
  };
};

const defaultPayoutSort = {
  field: "created_at",
  direction: "desc",
} as IElasticsearchSort;

export const usePayouts = (key: string, site: string, filters: any) => {
  const { clientId } = useClient(site);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const viewsPreference = useUserPreference("payout.list.filters");
  const aggregations = usePermitsAggregations(clientId);
  const omnifilters = useOmniFilter(usePermitsFilters(viewsPreference, aggregations));
  const [refreshCursor, setRefreshCursor] = usePersistedState(`${key}.refreshCursor`, []);
  const rowBuilder = (hits: any) => {
    return _.flatten(hits).map((item: any) => ({ ...item._source }));
  };
  const [sort, setSort] = useState<IElasticsearchSort>(defaultPayoutSort);
  const streamSort = useMemo(() => ElasticSearch.sort(sort), [sort]);
  const streamReverseSort = useMemo(() => ElasticSearch.reverseSort(sort), [sort]);
  const searchFn = fetchPayouts(networkHeaders);
  const streamQuery = useStreamQuery({
    key: key,
    size: 10,
    refreshCursor,
    setRefreshCursor,
    filter: filters,
    reverseSort: streamReverseSort,
    sort: streamSort,
    enabled: !!clientId,
    queryFn: searchFn,
    setHits: () => () => {
      return;
    },
  });
  const rows = useMemo(() => rowBuilder(streamQuery.hitsWindow), [streamQuery.hitsWindow]);
  return {
    streamQuery,
    rows,
    defaultSort,
    sort,
    setSort,
    omnifilters,
  };
};

const defaultRenewalsSort = {
  field: "id",
  direction: "desc",
} as IElasticsearchSort;

export const useRenewals = (key: string, site: string, filters?: any) => {
  const { clientId } = useClient(site);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const viewsPreference = useUserPreference("renewal.list.filters");
  const aggregations = usePermitsAggregations(clientId);
  const omnifilters = useOmniFilter(usePermitsFilters(viewsPreference, aggregations));
  const [refreshCursor, setRefreshCursor] = useState([]);
  const rowBuilder = (hits: any) => {
    return _.flatten(hits).map((item: any) => ({ ...item._source }));
  };
  const [sort, setSort] = useState<IElasticsearchSort>(defaultRenewalsSort);
  const streamSort = useMemo(() => ElasticSearch.sort(sort), [sort]);
  const streamReverseSort = useMemo(() => ElasticSearch.reverseSort(sort), [sort]);
  const searchFn = fetchRenewalSearchPermits(networkHeaders);
  const streamQuery = useRenewalStreamQuery({
    key: key,
    size: 30,
    refreshCursor,
    setRefreshCursor,
    filter: filters,
    reverseSort: streamReverseSort,
    sort: streamSort,
    enabled: !!clientId,
    queryFn: searchFn,
    setHits: () => () => {
      return;
    },
  });
  const rows = useMemo(() => rowBuilder(streamQuery.hitsWindow), [streamQuery.hitsWindow]);
  return {
    streamQuery,
    rows,
    defaultSort,
    sort,
    setSort,
    omnifilters,
  };
};

export const useInspections = (
  key: string,
  site: string,
  filters: any,
  sortingConfig: InspectionsCustomSortingConfiguration,
) => {
  const { clientId } = useClient(site);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const viewsPreference = useUserPreference("inspection.list.filters");
  const aggregations = usePermitsAggregations(clientId);
  const omnifilters = useOmniFilter(usePermitsFilters(viewsPreference, aggregations));
  const [refreshCursor, setRefreshCursor] = useState([]);
  const rowBuilder = (hits: any) => {
    return _.flatten(hits).map((item: any) => ({ ...item._source }));
  };

  const [sort, setSort] = useState(sortingConfig);

  const streamReverseSort = useMemo(() => ElasticSearch.reverseSort(sort), [sort]);

  const searchFn = fetchInspections(networkHeaders);
  const streamQuery = useStreamQuery({
    key: key,
    size: 30,
    refreshCursor,
    setRefreshCursor,
    filter: filters,
    reverseSort: streamReverseSort,
    sort: sort,
    enabled: !!clientId,
    queryFn: searchFn,
    setHits: () => () => {
      return;
    },
  });
  const rows = useMemo(() => rowBuilder(streamQuery.hitsWindow), [streamQuery.hitsWindow]);
  return {
    streamQuery,
    rows,
    defaultSort,
    sort,
    setSort,
    omnifilters,
  };
};
